import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Toaster as Sonner, type ToasterProps } from "sonner";

function useDocumentTheme(): "light" | "dark" {
  const [isDark, setIsDark] = useState(() => {
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("dark");
  });
  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains("dark"));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return isDark ? "dark" : "light";
}

function Toaster(props: ToasterProps) {
  const theme = useDocumentTheme();
  return (
    <Sonner
      position="top-center"
      theme={theme}
      closeButton
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      toastOptions={{
        classNames: {
          toast: "group toast !border shadow-lg !rounded-xl",
          title: "font-semibold text-sm",
          description: "!text-sm opacity-80",
          success:
            "!bg-[color-mix(in_oklch,var(--primary)_15%,var(--background))] !text-primary !border-[color-mix(in_oklch,var(--primary)_30%,var(--background))] [&>[data-icon]]:!text-primary",
          error:
            "!bg-[color-mix(in_oklch,var(--destructive)_12%,var(--background))] !text-destructive !border-[color-mix(in_oklch,var(--destructive)_25%,var(--background))] [&>[data-icon]]:!text-destructive",
          info: "!bg-card !text-foreground !border-border [&>[data-icon]]:!text-foreground",
          warning:
            "!bg-[color-mix(in_oklch,var(--accent)_30%,var(--background))] !text-accent-foreground !border-[color-mix(in_oklch,var(--accent)_50%,var(--background))] [&>[data-icon]]:!text-accent-foreground",
          actionButton:
            "!bg-foreground/10 !text-current !rounded-md hover:!bg-foreground/20",
          cancelButton:
            "!bg-foreground/10 !text-current !rounded-md hover:!bg-foreground/20",
          closeButton:
            "!bg-black/10 !border-0 !text-current hover:!bg-black/20",
        },
      }}
      {...props}
    />
  );
}

export { Toaster };
